import type {
  QuickSearchAddToDashboardRequest,
  QuickSearchAddToDashboardResponse,
  QuickSearchChannel,
  QuickSearchExportRequest,
  QuickSearchExportResponse,
  QuickSearchHistoryDetails,
  QuickSearchHistoryEntry,
  QuickSearchHistoryEntryRef,
  QuickSearchJob,
  QuickSearchJobRef,
  QuickSearchRequest,
} from '@/data-sources/contract';
import { api, overrideExisting } from '@/store/api/api';
import { fromSource, type Sourced } from '@/store/api/fromSource';
import { tag } from '@/store/api/tags';

export const quickSearchApi = api.injectEndpoints({
  overrideExisting,
  endpoints: (build) => ({
    listQuickSearchChannels: build.query<QuickSearchChannel[], Sourced<void>>({
      queryFn: fromSource((source, _params, ctx) => source.listQuickSearchChannels(ctx)),
      keepUnusedDataFor: Infinity,
    }),
    startQuickSearch: build.mutation<QuickSearchJobRef, Sourced<QuickSearchRequest>>({
      queryFn: fromSource((source, params, ctx) => source.startQuickSearch(params, ctx)),
    }),
    getQuickSearchJob: build.query<QuickSearchJob, Sourced<QuickSearchJobRef>>({
      queryFn: fromSource((source, params, ctx) => source.getQuickSearchJob(params, ctx)),
      providesTags: (_result, _error, arg) => [
        tag('QuickSearch', arg.source, `job:${arg.params.jobId}`),
      ],
    }),
    listQuickSearchHistory: build.query<QuickSearchHistoryEntry[], Sourced<void>>({
      queryFn: fromSource((source, _params, ctx) => source.listQuickSearchHistory(ctx)),
      providesTags: (_result, _error, arg) => [tag('QuickSearch', arg.source, 'HISTORY')],
    }),
    getQuickSearchHistoryEntry: build.query<
      QuickSearchHistoryDetails,
      Sourced<QuickSearchHistoryEntryRef>
    >({
      queryFn: fromSource((source, params, ctx) => source.getQuickSearchHistoryEntry(params, ctx)),
      providesTags: (_result, _error, arg) => [
        tag('QuickSearch', arg.source, `history:${arg.params.id}`),
      ],
    }),
    deleteQuickSearchHistoryEntry: build.mutation<null, Sourced<QuickSearchHistoryEntryRef>>({
      queryFn: fromSource(async (source, params, ctx) => {
        await source.deleteQuickSearchHistoryEntry(params, ctx);
        return null;
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tag('QuickSearch', arg.source, 'HISTORY'),
              tag('QuickSearch', arg.source, `history:${arg.params.id}`),
            ],
    }),
    addQuickSearchResultsToDashboard: build.mutation<
      QuickSearchAddToDashboardResponse,
      Sourced<QuickSearchAddToDashboardRequest>
    >({
      queryFn: fromSource((source, params, ctx) =>
        source.addQuickSearchResultsToDashboard(params, ctx),
      ),
      /* Each post added is charged at once, so the sidebar's balance moves. */
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [tag('QuickSearch', arg.source), tag('Credits', arg.source)],
    }),
    exportQuickSearchResults: build.mutation<
      QuickSearchExportResponse,
      Sourced<QuickSearchExportRequest>
    >({
      queryFn: fromSource((source, params, ctx) => source.exportQuickSearchResults(params, ctx)),
    }),
  }),
});

export const {
  useListQuickSearchChannelsQuery,
  useStartQuickSearchMutation,
  useGetQuickSearchJobQuery,
  useListQuickSearchHistoryQuery,
  useGetQuickSearchHistoryEntryQuery,
  useDeleteQuickSearchHistoryEntryMutation,
  useAddQuickSearchResultsToDashboardMutation,
  useExportQuickSearchResultsMutation,
} = quickSearchApi;
